import React, { useState, useEffect } from 'react';

import { Box, Tab, Tabs } from '@material-ui/core';
import { getDeerEngagementTemplatePreviewRoute, getDeerEngagementTemplateRoute } from 'Deer/constants/nonDeerRoutes';
import { setL2Visible } from 'Deer/slice/DeerNav.slice';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Container } from 'styles/admin';
import { VerticallyAlignedFlexContainer } from 'styles/common';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { ENGAGEMENT_TEMPLATE_MENU_OPTIONS } from 'constants/ProjectConstants';
import { getColorFromKey } from 'constants/helper';
import { KH_LINKS } from 'constants/knowledgeHubLinks';
import { routes } from 'constants/routes';

import { showErrorSnackbar, showSuccessSnackbar } from 'services/snackbar';
import {
  getActiveTemplates,
  getSampleTemplates,
  getArchivedTemplates,
  archiveTemplate,
  duplicateTemplate,
} from 'services/templates/engagement';

import TooltipInfo from 'uiToolkit/TooltipInfo';
import { Typography17 } from 'uiToolkit/Typography';
import { TextHeader2 } from 'uiToolkitV1/Typography';

import TemplateList from '../../../common/TemplateCommons/TemplateList';

const TAB_TYPES = {
  ACTIVE: 'ACTIVE',
  SAMPLE: 'SAMPLE',
  ARCHIVED: 'ARCHIVED',
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

const EngagementTemplateList = () => {
  const [loading, setLoading] = useState(false);
  const [activeTemplates, setActiveTemplates] = useState([]);
  const [sampleTemplates, setSampleTemplates] = useState([]);
  const [archivedTemplates, setArchivedTemplates] = useState([]);
  // this index one in required just to make it work with mui tabs which only support index based,
  // else currentTabLabel is better way: having a string identifier instead of relying on index only
  const [currentTab, setCurrentTab] = useState(0);
  const [currentTabLabel, setCurrentTabLabel] = useState(TAB_TYPES.ACTIVE);

  const dispatch = useDispatch();
  const history = useHistory();

  const getEngagementTemplates = ({ label, templateSetter }) => {
    const getTemplateService =
      label === TAB_TYPES.ACTIVE
        ? getActiveTemplates
        : label === TAB_TYPES.SAMPLE
          ? getSampleTemplates
          : getArchivedTemplates;

    setLoading(true);
    templateSetter([]);
    getTemplateService({ offset: 0, limit: 500 })
      .then((res) => {
        if (res.data?.length) {
          templateSetter(res.data);
        }
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message || 'Sorry, unable to fetch the templates!'));
      })
      .finally(() => setLoading(false));
  };

  const getData = (currentTabLabel) => {
    if (currentTabLabel === TAB_TYPES.ACTIVE) {
      getEngagementTemplates({ label: TAB_TYPES.ACTIVE, templateSetter: setActiveTemplates });
    } else if (currentTabLabel === TAB_TYPES.SAMPLE) {
      getEngagementTemplates({ label: TAB_TYPES.SAMPLE, templateSetter: setSampleTemplates });
    } else if (currentTabLabel === TAB_TYPES.ARCHIVED) {
      getEngagementTemplates({ label: TAB_TYPES.ARCHIVED, templateSetter: setArchivedTemplates });
    }
  };

  useEffect(() => {
    getData(currentTabLabel);
  }, [currentTabLabel]);

  const onNewClick = () => {
    const routeToGo = checkNewNavEnabled()
      ? getDeerEngagementTemplateRoute('new')
      : routes.template.engagement.index.replace(':templateId', 'new');

    if (checkNewNavEnabled()) {
      dispatch(setL2Visible({ isL2Visible: false }));
    }
    history.push(routeToGo);
  };

  const handleOpenTemplate = (id, type) => {
    const routeToGo = checkNewNavEnabled()
      ? getDeerEngagementTemplateRoute(id)
      : routes.template.engagement.index.replace(':templateId', id);
    if (checkNewNavEnabled()) {
      dispatch(setL2Visible({ isL2Visible: false }));
    }
    history.push(routeToGo);
  };

  const handleOpenSampleTemplate = (id) => {
    const path = checkNewNavEnabled()
      ? getDeerEngagementTemplatePreviewRoute(id)
      : routes.template.engagement.preview.replace(':templateId', id);
    window.open(path, '_blank');
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setCurrentTabLabel(Object.values(TAB_TYPES).filter((val, index) => index === newValue)[0]);
  };

  const handleArchiveTemplate = (templateId, isArchive) => {
    setLoading(true);

    archiveTemplate({ templateId, isArchive })
      .then((res) => {
        if (isArchive) {
          dispatch(showSuccessSnackbar('Template archived successfully!'));
        } else {
          dispatch(showSuccessSnackbar('Template unarchived successfully!'));
        }
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message || 'Sorry, unable to archive the template!'));
      })
      .finally(() => {
        // setLoading(false);
        getData(currentTabLabel);
      });
  };

  const handleDuplicateTemplate = (templateId) => {
    setLoading(true);

    duplicateTemplate({ templateId })
      .then((res) => {
        dispatch(showSuccessSnackbar('Template duplicated successfully!'));
        const templateRoute = checkNewNavEnabled()
          ? getDeerEngagementTemplateRoute(res.id)
          : routes.template.engagement.index.replace(':templateId', res.id);
        history.push(templateRoute);
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message || 'Sorry, unable to duplicate the template!'));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <StyledContainer>
      <VerticallyAlignedFlexContainer style={{ justifyContent: 'space-between' }}>
        <div>
          <VerticallyAlignedFlexContainer bottomGutter={2}>
            <TextHeader2 bold>Templates</TextHeader2>
            <TooltipInfo
              leftGutter={1}
              title={KH_LINKS.ENGAGEMENT_TEMPLATES.description}
              linkUrl={KH_LINKS.ENGAGEMENT_TEMPLATES.url}
            />
          </VerticallyAlignedFlexContainer>
          <Typography17>Design and view sample / archived templates for your engagement surveys</Typography17>
        </div>
        <Button blue size="large" onClick={onNewClick} disabled={loading}>
          Add Template
        </Button>
      </VerticallyAlignedFlexContainer>

      <FormContainer>
        <StyledTabs value={currentTab} onChange={handleTabChange}>
          <StyledTab label="Active" />
          <StyledTab label="Sample" />
          <StyledTab label="Archived" />
        </StyledTabs>
        <TabPanel value={currentTab} index={0}>
          <TemplateList
            templates={activeTemplates}
            loading={loading}
            menuOptions={ENGAGEMENT_TEMPLATE_MENU_OPTIONS.active}
            updateData={() => getData(TAB_TYPES.ACTIVE)}
            noDataMessage="No active templates were found"
            noDataSubMessage='click on "Add Template" to get started'
            handleOpenTemplate={handleOpenTemplate}
            currentTab={TAB_TYPES.ACTIVE}
            handleArchiveTemplate={handleArchiveTemplate}
            handleDuplicateTemplate={handleDuplicateTemplate}
          />
        </TabPanel>
        <TabPanel value={currentTab} index={1}>
          <TemplateList
            templates={sampleTemplates}
            loading={loading}
            updateData={() => getData(TAB_TYPES.SAMPLE)}
            noDataMessage="No sample templates were found"
            noDataSubMessage='click on "Add Template" to get started'
            handleOpenTemplate={handleOpenSampleTemplate}
            currentTab={TAB_TYPES.SAMPLE}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <TemplateList
            templates={archivedTemplates}
            loading={loading}
            menuOptions={ENGAGEMENT_TEMPLATE_MENU_OPTIONS.archived}
            updateData={() => getData(TAB_TYPES.ARCHIVED)}
            rowClickable={false}
            noDataMessage="No archived templates were found"
            handleOpenTemplate={handleOpenTemplate}
            currentTab={TAB_TYPES.ARCHIVED}
            handleArchiveTemplate={handleArchiveTemplate}
            handleDuplicateTemplate={handleDuplicateTemplate}
          />
        </TabPanel>
      </FormContainer>
    </StyledContainer>
  );
};

const StyledTabs = styled(Tabs)`
  border-bottom: 1px solid ${getColorFromKey('lightGrey')};
  .MuiTabs-indicator {
    height: 3px;
    background: ${getColorFromKey('primary')} !important;
  }
`;

const StyledTab = styled(Tab)`
  font-size: 1rem;
  min-width: auto;
  margin-left: 1.5rem;
  padding: 14px 12px 12px 12px;
  .MuiTab-wrapper {
    text-transform: initial;
  }
  &.Mui-selected {
    font-weight: 600;
    color: ${getColorFromKey('primary')};
  }
`;

const StyledContainer = styled(Container)`
  padding: 16px;
  height: max-content;
`;

const FormContainer = styled.div`
  background: white;
  border: 1px solid ${getColorFromKey('lightGrey')};
  margin-top: 1.5rem;
  margin-bottom: 2.5rem;
`;

export default EngagementTemplateList;
